import { useState, useEffect, useCallback } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useThemeColors } from '../hooks/useThemeColors'
import {
  createDesignPartnerAgreement,
  getAdminDesignPartnerAgreements,
  updateDesignPartnerAgreementStatus,
  deleteDesignPartnerAgreement,
  getDesignPartnerAgreementPdfUrl,
  type DesignPartnerAgreementAdmin,
  type DesignPartnerAgreementAdminStatus,
  type NewDesignPartnerAgreement,
} from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days < 1) return 'today'
  if (days === 1) return '1d ago'
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const STATUS_LABEL: Record<DesignPartnerAgreementAdmin['status'], string> = {
  draft: 'Draft',
  sent: 'Sent — awaiting signature',
  signed: 'Signed',
  paid: 'Paid',
  expired: 'Expired',
  terminated: 'Terminated',
  completed: 'Completed',
}

// Matt, 2026-08-14: admin-manageable subset — the dropdown lets Matt move an
// agreement between these four manually. 'draft'/'signed'/'expired' remain
// automated-only states set by the click-to-sign/pay flow itself and are not
// offered here (they still display correctly via STATUS_LABEL if a row is
// currently in one of them).
const ADMIN_SETTABLE_STATUSES: DesignPartnerAgreementAdminStatus[] = ['sent', 'paid', 'terminated', 'completed']

function statusBadgeClass(status: DesignPartnerAgreementAdmin['status']): string {
  const map: Record<string, string> = {
    draft: 'bg-carbon text-steel',
    sent: 'bg-signal/20 text-signal',
    signed: 'bg-green-500/20 text-green-400',
    paid: 'bg-green-500/20 text-green-400',
    expired: 'bg-red-500/20 text-red-400',
    terminated: 'bg-red-500/20 text-red-400',
    completed: 'bg-green-500/20 text-green-400',
  }
  return map[status] ?? 'bg-carbon text-steel'
}

// ─── Create Agreement Modal ───────────────────────────────────────────────────

interface CreateModalProps {
  onClose: () => void
  onSuccess: (agreement: { shareUrl: string }) => void
}

function CreateAgreementModal({ onClose, onSuccess }: CreateModalProps) {
  const c = useThemeColors()
  const [companyLegalName, setCompanyLegalName] = useState('')
  const [useCase, setUseCase] = useState('')
  const [valueOfSolving, setValueOfSolving] = useState('')
  const [targetMetrics, setTargetMetrics] = useState('')
  const [scopeOfWork, setScopeOfWork] = useState('')
  const [estimatedCostToDeliver, setEstimatedCostToDeliver] = useState('')
  // Matt, 2026-08-14: default token payment is $500 (corrected from ~$10K).
  const [priceUsd, setPriceUsd] = useState('500')
  const [publicReferenceOptOut, setPublicReferenceOptOut] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmit = companyLegalName.trim() && useCase.trim() && targetMetrics.trim() && !saving

  async function handleSubmit() {
    if (!canSubmit) return
    setSaving(true)
    setError(null)
    try {
      const body: NewDesignPartnerAgreement = {
        companyLegalName: companyLegalName.trim(),
        useCase: useCase.trim(),
        valueOfSolving: valueOfSolving.trim() || null,
        targetMetrics: targetMetrics.trim(),
        scopeOfWork: scopeOfWork.trim() || null,
        estimatedCostToDeliver: estimatedCostToDeliver.trim() || null,
        priceUsd: priceUsd.trim() ? Number(priceUsd) : null,
        publicReferenceOptOut,
      }
      const result = await createDesignPartnerAgreement(body)
      onSuccess(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create agreement')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onClick={onClose}
    >
      <div
        style={{ background: c.bgSecondary, border: `1px solid ${c.border}`, borderRadius: 8, padding: 24, width: 480, maxWidth: '90vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, fontWeight: 600, color: c.textPrimary, marginBottom: 4 }}>New design partner agreement</h2>
        <p style={{ fontSize: 13, color: c.textMuted, marginBottom: 20 }}>
          Generates a shareable link. The customer clicks through to read the terms, sign, and (if priced) pay — no DocuSign needed.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>
            Company legal name
            <input
              type="text"
              value={companyLegalName}
              onChange={(e) => setCompanyLegalName(e.target.value)}
              placeholder="Acme Robotics, Inc."
              style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 6, border: `1px solid ${c.border}`, background: c.bgPrimary, color: c.textPrimary, fontSize: 14 }}
            />
          </label>

          <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>
            Use case
            <textarea
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              placeholder="Real-time defect detection on the assembly line camera feed"
              rows={2}
              style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 6, border: `1px solid ${c.border}`, background: c.bgPrimary, color: c.textPrimary, fontSize: 14, resize: 'vertical' }}
            />
          </label>

          <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>
            Value of solving this use-case
            <textarea
              value={valueOfSolving}
              onChange={(e) => setValueOfSolving(e.target.value)}
              placeholder="Avoids ~$2M/yr in incident costs and manual review time"
              rows={2}
              data-testid="value-of-solving-input"
              style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 6, border: `1px solid ${c.border}`, background: c.bgPrimary, color: c.textPrimary, fontSize: 14, resize: 'vertical' }}
            />
          </label>

          <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>
            Target metrics
            <textarea
              value={targetMetrics}
              onChange={(e) => setTargetMetrics(e.target.value)}
              placeholder="&lt;200ms end-to-end latency, 95%+ detection accuracy on held-out clips"
              rows={2}
              style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 6, border: `1px solid ${c.border}`, background: c.bgPrimary, color: c.textPrimary, fontSize: 14, resize: 'vertical' }}
            />
          </label>

          <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>
            Scope of work provided by Primate
            <textarea
              value={scopeOfWork}
              onChange={(e) => setScopeOfWork(e.target.value)}
              placeholder="Custom model tuning, 2 camera integrations, weekly check-ins"
              rows={2}
              data-testid="scope-of-work-input"
              style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 6, border: `1px solid ${c.border}`, background: c.bgPrimary, color: c.textPrimary, fontSize: 14, resize: 'vertical' }}
            />
          </label>

          <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>
            Estimated cost to deliver scope of work
            <textarea
              value={estimatedCostToDeliver}
              onChange={(e) => setEstimatedCostToDeliver(e.target.value)}
              placeholder="$15,000 engineering time"
              rows={2}
              data-testid="estimated-cost-input"
              style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 6, border: `1px solid ${c.border}`, background: c.bgPrimary, color: c.textPrimary, fontSize: 14, resize: 'vertical' }}
            />
          </label>

          <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>
            Token payment (USD — default $500; clear for a fully complimentary pilot)
            <input
              type="number"
              min={0}
              step="0.01"
              value={priceUsd}
              onChange={(e) => setPriceUsd(e.target.value)}
              placeholder="500.00"
              style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 6, border: `1px solid ${c.border}`, background: c.bgPrimary, color: c.textPrimary, fontSize: 14 }}
            />
          </label>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: c.textMuted, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={publicReferenceOptOut}
              onChange={(e) => setPublicReferenceOptOut(e.target.checked)}
              data-testid="public-reference-optout-input"
              style={{ marginTop: 2 }}
            />
            <span>
              Partner opts OUT of public logo/name/quote use (website, case study, launch materials).
              Private reference for investor materials is always retained regardless of this choice.
            </span>
          </label>
        </div>

        {error && <p style={{ color: '#ef4444', fontSize: 13, marginTop: 12 }}>{error}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${c.border}`, background: 'none', color: c.textMuted, fontSize: 14, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: canSubmit ? c.signal : c.carbon, color: '#fff', fontSize: 14, fontWeight: 600, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
          >
            {saving ? 'Creating…' : 'Create agreement'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Share Link Success Panel ─────────────────────────────────────────────────

function ShareLinkPanel({ shareUrl, onClose }: { shareUrl: string; onClose: () => void }) {
  const c = useThemeColors()
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onClick={onClose}
    >
      <div
        style={{ background: c.bgSecondary, border: `1px solid ${c.border}`, borderRadius: 8, padding: 24, width: 480, maxWidth: '90vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, fontWeight: 600, color: c.textPrimary, marginBottom: 4 }}>Agreement created</h2>
        <p style={{ fontSize: 13, color: c.textMuted, marginBottom: 16 }}>
          Send this link to the customer. They'll see the terms, sign with a click, and (if priced) pay via Stripe Checkout — all on this one link.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            readOnly
            value={shareUrl}
            style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: `1px solid ${c.border}`, background: c.bgPrimary, color: c.textPrimary, fontSize: 13 }}
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={handleCopy}
            style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: c.signal, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${c.border}`, background: 'none', color: c.textMuted, fontSize: 14, cursor: 'pointer' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminDesignPartners() {
  const c = useThemeColors()
  const { user, isLoaded } = useUser()
  const [agreements, setAgreements] = useState<DesignPartnerAgreementAdmin[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? ''
  const isAdmin = isLoaded && !!user && ADMIN_EMAILS.includes(email)

  const load = useCallback(() => {
    setLoading(true)
    getAdminDesignPartnerAgreements()
      .then((res) => setAgreements(res.agreements))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!isAdmin) return
    load()
  }, [isAdmin, load])

  async function handleStatusChange(id: string, status: DesignPartnerAgreementAdminStatus) {
    setRowError(null)
    setStatusUpdatingId(id)
    const prev = agreements
    // Optimistic update so the dropdown doesn't visually snap back while the request is in flight.
    setAgreements((rows) => rows.map((a) => (a.id === id ? { ...a, status } : a)))
    try {
      await updateDesignPartnerAgreementStatus(id, status)
    } catch (err) {
      setAgreements(prev)
      setRowError(err instanceof Error ? err.message : 'Failed to update status')
    } finally {
      setStatusUpdatingId(null)
    }
  }

  async function handleDelete(id: string, companyLegalName: string) {
    if (!window.confirm(`Delete the agreement for "${companyLegalName}"? This permanently removes the row and its signed PDF (if any). This cannot be undone.`)) {
      return
    }
    setRowError(null)
    setDeletingId(id)
    try {
      await deleteDesignPartnerAgreement(id)
      setAgreements((rows) => rows.filter((a) => a.id !== id))
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete agreement')
    } finally {
      setDeletingId(null)
    }
  }

  async function handleViewPdf(id: string) {
    setRowError(null)
    setPdfLoadingId(id)
    try {
      const { url } = await getDesignPartnerAgreementPdfUrl(id)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'No signed PDF available yet')
    } finally {
      setPdfLoadingId(null)
    }
  }

  if (!isLoaded) return <div className="min-h-screen bg-void" />

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <p className="text-body text-steel">Access denied</p>
      </div>
    )
  }

  return (
    <div className="p-8 space-y-6 max-w-5xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-h1 text-chalk">Design Partner Agreements</h1>
          <p className="text-body-sm text-steel mt-1">
            Create a click-to-sign, click-to-pay pilot agreement link for a customer — replaces DocuSign.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{ padding: '10px 18px', borderRadius: 6, border: 'none', background: c.signal, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          + New agreement
        </button>
      </div>

      {rowError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded px-4 py-2 text-footnote text-red-400">
          {rowError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-signal" />
        </div>
      ) : agreements.length === 0 ? (
        <div className="bg-obsidian border border-carbon rounded p-8 text-center">
          <p className="text-steel text-body">No agreements yet. Click "+ New agreement" to create your first one.</p>
        </div>
      ) : (
        <div className="bg-obsidian border border-carbon rounded overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm table-fixed">
              <thead>
                <tr className="border-b border-carbon">
                  <th className="text-left px-3 py-3 text-footnote text-steel font-medium w-[15%]">Company</th>
                  <th className="text-left px-3 py-3 text-footnote text-steel font-medium w-[22%]">Use Case</th>
                  <th className="text-left px-3 py-3 text-footnote text-steel font-medium w-[9%]">Fee</th>
                  <th className="text-left px-3 py-3 text-footnote text-steel font-medium w-[10%]">Public Ref.</th>
                  <th className="text-left px-3 py-3 text-footnote text-steel font-medium w-[16%]">Status</th>
                  <th className="text-left px-3 py-3 text-footnote text-steel font-medium w-[9%]">Created</th>
                  <th className="text-left px-3 py-3 text-footnote text-steel font-medium w-[9%]">Link</th>
                  <th className="text-left px-3 py-3 text-footnote text-steel font-medium w-[6%]">PDF</th>
                  <th className="text-left px-3 py-3 text-footnote text-steel font-medium w-[4%]"></th>
                </tr>
              </thead>
              <tbody>
                {agreements.map((a, i) => (
                  <tr key={a.id} className={`border-b border-carbon/50 ${i % 2 === 0 ? '' : 'bg-carbon/10'}`}>
                    <td className="px-3 py-3 text-chalk font-medium truncate">{a.companyLegalName}</td>
                    <td className="px-3 py-3 text-steel"><span className="line-clamp-2">{a.useCase}</span></td>
                    <td className="px-3 py-3 text-steel whitespace-nowrap">{a.priceUsd ? `$${a.priceUsd.toFixed(2)}` : 'Free'}</td>
                    <td className="px-3 py-3 text-steel truncate">{a.publicReferenceOptOut ? 'Opted out' : 'Approved'}</td>
                    <td className="px-3 py-3">
                      <select
                        value={ADMIN_SETTABLE_STATUSES.includes(a.status as DesignPartnerAgreementAdminStatus) ? a.status : ''}
                        onChange={(e) => handleStatusChange(a.id, e.target.value as DesignPartnerAgreementAdminStatus)}
                        disabled={statusUpdatingId === a.id}
                        aria-label={`Status for ${a.companyLegalName}`}
                        className={`w-full px-1.5 py-0.5 rounded text-footnote font-medium border-0 cursor-pointer ${statusBadgeClass(a.status)}`}
                        style={{ opacity: statusUpdatingId === a.id ? 0.6 : 1 }}
                      >
                        {!ADMIN_SETTABLE_STATUSES.includes(a.status as DesignPartnerAgreementAdminStatus) && (
                          <option value="" disabled>{STATUS_LABEL[a.status]}</option>
                        )}
                        {ADMIN_SETTABLE_STATUSES.map((s) => (
                          <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-3 text-steel/60 whitespace-nowrap text-footnote">{timeAgo(a.createdAt)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <button
                        onClick={() => navigator.clipboard.writeText(a.shareUrl)}
                        style={{ fontSize: 12, color: c.signal, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        Copy link
                      </button>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {a.signedAt ? (
                        <button
                          onClick={() => handleViewPdf(a.id)}
                          disabled={pdfLoadingId === a.id}
                          title="View signed PDF"
                          aria-label={`View signed PDF for ${a.companyLegalName}`}
                          style={{ background: 'none', border: 'none', cursor: pdfLoadingId === a.id ? 'default' : 'pointer', padding: 0, color: c.signal, opacity: pdfLoadingId === a.id ? 0.5 : 1 }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <path d="M14 2v6h6" />
                          </svg>
                        </button>
                      ) : (
                        <span className="text-steel/40 text-footnote">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <button
                        onClick={() => handleDelete(a.id, a.companyLegalName)}
                        disabled={deletingId === a.id}
                        title="Delete agreement"
                        aria-label={`Delete agreement for ${a.companyLegalName}`}
                        style={{ background: 'none', border: 'none', cursor: deletingId === a.id ? 'default' : 'pointer', padding: 0, color: '#ef4444', opacity: deletingId === a.id ? 0.5 : 1 }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && (
        <CreateAgreementModal
          onClose={() => setShowCreate(false)}
          onSuccess={(result) => {
            setShowCreate(false)
            setShareUrl(result.shareUrl)
            load()
          }}
        />
      )}

      {shareUrl && <ShareLinkPanel shareUrl={shareUrl} onClose={() => setShareUrl(null)} />}
    </div>
  )
}
